if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = this.querySelector(".wt-slider__container");
      }

      connectedCallback() {
        this.lazyLoadPosters();
        this.init();
      }

      // Posters are rendered as data-poster so a dozen of them don't download during page load.
      lazyLoadPosters() {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(({target: video, isIntersecting}) => {
            if (!isIntersecting) return;
            video.poster = video.dataset.poster;
            video.removeAttribute("data-poster");
            observer.unobserve(video);
          });
        }, {rootMargin: "350px 100px"});
        this.posterObserver = observer;
        this.querySelectorAll("video[data-poster]").forEach(video => observer.observe(video));
      }

      prepareVideo(video) {
        if (video.dataset.loaded) return;
        video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        video.dataset.loaded = 'true';
        video.load();
      }

      disconnectedCallback() {
        this.removeEventListener("lv87:slider-ready", this.onSliderReady);
        this.sectionObserver?.disconnect();
        this.posterObserver?.disconnect();
        this.pasueAllVideos();
      }

      updateAllVideosSound(swiper) {
        const soundOn = swiper.el.dataset.sound === "on";
        swiper.slides.forEach((slide) => {
          let video = slide.querySelector("video");
          if (video) {
            video.muted = !soundOn;
          }
        });
      }

      observeSection() {
        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        };

        const sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting;
              const video = this.querySelector(".swiper-slide-active video");

              if (video) {
                if (entry.isIntersecting && !matchMedia("(prefers-reduced-motion: reduce)").matches && !navigator.connection?.saveData) {
                  this.prepareVideo(video);
                  video.play().catch(() => {});
                } else {
                  video.pause();
                }
              }
            });
          },
          observerOptions,
        );

        this.sectionObserver = sectionObserver;
        sectionObserver.observe(this);
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide, index) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          const video = slide.querySelector("video");
          const that = this;

          button.addEventListener("click", function () {
            if (!video) return;
            if (video.muted) {
              video.muted = false;
              swiper.el.dataset.sound = "on";
            } else {
              video.muted = true;
              swiper.el.dataset.sound = "off";
            }
            that.updateAllVideosSound(swiper);
          });
        });
      }

      pasueAllVideos() {
        const videos = this.querySelectorAll("video");
        videos.forEach((video) => {
          video.pause();
        });
      }

      playVideoInActiveSlide(swiper) {
        const sound = swiper.el.dataset.sound;
        const activeSlideVideo =
          this.findActiveSlide(swiper)?.querySelector("video");
        // Playing before the section is visible pulls a multi-MB HD stream during page load.
        if (activeSlideVideo && this.inView && !matchMedia("(prefers-reduced-motion: reduce)").matches && !navigator.connection?.saveData) {
          this.prepareVideo(activeSlideVideo);
          activeSlideVideo.play().catch(() => {});
          activeSlideVideo.muted = sound !== "on";
        }
      }

      findActiveSlide(swiper) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        return activeSlide;
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.remove(this.activeClass);

          if (activeSlide === slide) {
            slide.classList.add(this.activeClass);
          }
        });
      }

      handleSlideChange(swiper) {
        this.pasueAllVideos();
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      addVideoEventHandlers(swiper) {
        const that = this;
        swiper.on("slideChange", (swp) => that.handleSlideChange(swp));
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const mySwiperInstance = swiperContainer.swiper;

        if (mySwiperInstance && this.boundSwiper !== mySwiperInstance) {
          this.boundSwiper = mySwiperInstance;
          this.addVideoEventHandlers(mySwiperInstance);
          this.handleSlideChange(mySwiperInstance);
          this.handleSoundToggle(mySwiperInstance);
          this.observeSection();

        }
      }

      init() {
        this.onSliderReady = () => this.checkSwiperInitialization();
        this.addEventListener("lv87:slider-ready", this.onSliderReady);
        this.checkSwiperInitialization();
      }
    },
  );
}
